<template>
    <div
        class="reo-button button-container"
        :class="{
            'mobile': isMobile
        }"
        :style="customWidthCss"
    >
        <button
            :ga-label="gaLabel"
            :disabled="disabled"
            class='reo-button-inner'
            :class="[
                {
                    disabled: disabled,
                },
                colorType ?? 'dark',
                size,
                buttonClass
            ]"
            :data-transaction-name="dataTransactionName"
            @click="onButtonClick"
        >
            <div class="button-svg-icon" v-if="svgIcon">
                <slot name='svgIcon'></slot>
            </div>
            <div class="button-icon" v-if="icon">
                <slot name='icon'></slot>
            </div>
            <div class="button-label" :class="{'hasIcon': Number(icon) | Number(svgIcon) } ">
                <slot name="label">
                    {{ label }}
                </slot>

            </div>
        </button>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { ECommonButtonSize, ECommonButtonType } from '@reolink-cloud/interface';
import { RawLocation } from 'vue-router';
export default Vue.extend({
    props: {
        size: {
            type: String as PropType<ECommonButtonSize>,
            default: ECommonButtonSize.LARGE
        },
        colorType: {
            type: String as PropType<ECommonButtonType>,
            default: ECommonButtonType.DARK
        },
        customWidth: {
            type: [Number, String],
            default: ''
        },
        label: {
            type: String
        },
        icon: {
            type: Boolean,
            default: false
        },
        svgIcon: {
            type: Boolean,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        gaLabel: {
            type: String,
            default: ''
        },
        mobileScaleEnable: {
            type: Boolean,
            default: false
        },
        /**
         * 用于让按钮执行导航功能, 外部链接
         */
        href: {
            type: String
        },
        /**
         * href打开位置
         */
        target: {
            type: String,
            default: '_self'
        },
        /**
         * 用于让按钮执行导航功能，内部链接
         */
        to: {
            type: [Object, String] as PropType<RawLocation>,
        },
        buttonClass: {
            type: String,
            default: ''
        },
        dataTransactionName: String
    },
    computed: {
        isMobile(): boolean {

            return this.mobileScaleEnable && this.$store.getters.isMobile;

        },
        customWidthCss(): string {

            if ( this.customWidth ) {

                if ( typeof this.customWidth === 'string' && this.customWidth.includes( '%' )) {

                    return `--width: ${this.customWidth}`;

                } else {

                    return `--width: ${this.customWidth}px;`;

                }

            } else {

                return `--width: unset;`;

            }

        }
    },
    methods: {
        async onButtonClick() {

            if ( this.href ) {

                window.open( this.href, this.target );

            } else if ( this.to ) {

                await this.$router.push( this.to );

            }
            this.$emit( 'click' );

        }
    }
});
</script>

<style lang="less" scoped>
    .button-container{
        display: inline-block;
        width: var(--width);
        .reo-button-inner{
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: @border-radius-level-button;
            border: 1px solid @brand_light;
            overflow: hidden;
            .button-svg-icon{
                display: flex;
                align-items: center;
            }
            .button-label{
                text-wrap: wrap;
                &.hasIcon{
                    margin-left: 4px;
                }
            }
            &.light{
                background: transparent;
                color: @brand_light;
                transition-duration: .3s;
                &:hover{
                    @media @pc{
                        background: @brand_light;
                        color: @font_primary_dark;
                        cursor: pointer;
                    }
                    @media @mobile{
                        background: @brand_light;
                        color: @font_primary_dark;
                        cursor: pointer;
                    }
                }
                &:active{
                    background: @brand_light_click;
                    border-color: @brand_light_click;
                }
                &.disabled{
                    border-color: rgba(118, 118, 128, 0.24);
                    color: @font_disable;
                    cursor:not-allowed;
                    .button-svg-icon{
                        color: @font_disable;
                    }
                }

            }
            &.dark{
                background: @brand_light;
                color: @font_primary_dark;
                transition-duration: .3s;
                &:hover{
                    @media @pc{
                        background: @brand_light_hover;
                        cursor: pointer;
                    }
                    @media @mobile{
                        background: @brand_light_hover;
                        cursor: pointer;
                    }
                    .button-svg-icon{
                        fill: #fff;
                    }
                }
                &:active{
                    background: @brand_light_click;
                    border-color: @brand_light_click;
                    .button-svg-icon{
                        fill: #fff;
                    }
                }
                &.disabled{
                    border-color: rgba(118, 118, 128, 0.24);
                    background: rgba(118, 118, 128, 0.24);
                    /** border不和background叠加 */
                    background-clip: padding-box;
                    color: @font_disable;
                    .button-svg-icon{
                        fill: @font_disable;
                    }
                }
                .button-svg-icon{
                    fill: @brand_light;
                }
            }
            &.red{
                background: transparent;
                color: @red_5;
                transition-duration: .3s;
                border-color: @red_5;
                &:hover{
                    @media @pc{
                        background: @red_5;
                        color: @font_primary_dark;
                        cursor: pointer;
                    }
                    @media @mobile{
                        background: @red_5;
                        color: @font_primary_dark;
                        cursor: pointer;
                    }
                }
                &:active{
                    background: @red_5;
                    border-color: @red_5;
                }
                &.disabled{
                    border-color: rgba(118, 118, 128, 0.24);
                    color: @font_disable;
                    .button-svg-icon{
                        color: @font_disable;
                    }
                }

            }
            &.large{
                padding: 12px 24px;
                font-size: 14px;
                line-height: 22px;
                font-weight: 500;
                .button-svg-icon{
                    svg{
                        width: 22px;
                        height: 22px;
                    }
                }
                .button-icon{
                    font-size: 22px;
                }
            }
            &.middle{
                padding: 10px 16px;
                font-size: 14px;
                line-height: 20px;
                font-weight: 500;
                .button-svg-icon{
                    svg{
                        width: 20px;
                        height: 20px;
                    }
                }
                .button-icon{
                    font-size: 20px;
                }
            }
            &.small{
                padding: 6px 12px;
                font-size: 14px;
                line-height: 20px;
                font-weight: 500;
                .button-svg-icon{
                    svg{
                        width: 20px;
                        height: 20px;
                    }
                }
                .button-icon{
                    font-size: 20px;
                }
            }
        }
        &.mobile{
            transform: scale(0.8);
        }
    }

    .coupon-input-box {
        .reo-button {
            width: auto;
            .reo-button-inner {
                border-radius: 0 4px 4px 0;
                padding: 5px 15px;
            }
        }

        .coupon-input-box-inner {
            .reo-button {
                .reo-button-inner {
                    border-radius: 0 8px 8px 0;
                    padding: 13px 15px;
                    border: none;
                    .button-label {
                        font-size: 16px;
                        font-weight: 500;
                    }
                }
            }
        }
    }

</style>
